function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
}

/**
 * Call blur on an element from an event
 * @param ev HTML Event
 */
function blur(ev: Event): void {
    if (!(ev.target instanceof HTMLInputElement)) return;
    ev.target.blur();
}

/**
 * Get the string value of an input element from an event
 * @param {Event} ev HTML  Event
 * @returns {string}
 */
function getInputValue(ev: Event): string {
    if (!(ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement)) return '';
    return ev.target.value;
}

/**
 * Retrieves the value of an input element as a number.
 *
 * @param {Event} ev - The event object containing the input element.
 * @returns {number} The value of the input element as a number.
 */
function getInputValueNumber(ev: Event): number {
    if (!(ev.target instanceof HTMLInputElement)) return 0;
    return ev.target.valueAsNumber;
}

function sortArrayByProperty<T extends object>(arr: T[], property: keyof T): T[] {
    arr.sort((a, b) => {
        const aValue = a[property];
        const bValue = b[property];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return aValue - bValue;
        }

        if (aValue instanceof Date && bValue instanceof Date) {
            return aValue.getTime() - bValue.getTime();
        }

        // if one of them is a string, handle both as strings
        if ((typeof aValue === 'string' && typeof bValue !== 'string') || (typeof aValue !== 'string' && typeof bValue === 'string')) {
            return (aValue as any).toString().localeCompare((bValue as any).toString());
        }

        const ap = Number(aValue);
        const bp = Number(bValue);

        if (!Number.isNaN(ap) && !Number.isNaN(bp)) {
            return ap - bp;
        }

        return 0;
    });

    return arr;
}

/** Calculate the average safely
 * @param {number} sum
 * @param {number} count
*/
function safeAverage(sum: number, count: number): number {
    return count > 0 ? sum / count : 0;
}

/**
 * Converts a number to a percentage by dividing it by 100.
 * @param {number} value
 * @returns {number}
 */
function formatAsPercentage(value: number): number {
    return value / 100;
}

/**
 * Rounds a given number up to the nearest integer.
 * @param {number} value
 * @returns {number}
 */
function formatAsRoundedNumber(value: number): number {
    return Math.round(value);
}

/**
 * Computes the percentage of a given 'part' in relation to the 'total' value then rounds to the nearest whole number.
 * @param {number} part
 * @param {number} total
 * @returns {number}
 * @deprecated Dont use this is dangerous calculations in the frontend.
 *
 */
function getPercentage(part: number, total: number): number {
    const percentage = formatAsRoundedNumber((Math.abs(part) / total) * 100);
    return percentage;
}

export function getPercentageSafe(value: number, total: number): number {
    if (total === 0) return 0;
    if (total < 0) return 0;
    const percentage = value / total;
    if (!Number.isFinite(percentage)) return 0;
    return percentage;
}

/**
 * Check if a value is a number
 * that it is not nan and is finite
 *
 * @param {any} value
 * @return {boolean}
 */
function isNumber(value: unknown): boolean {
    if (typeof value === 'number') return true;
    return Number.isFinite(value);
}

/**
 * Check if a value is empty
 * null, undefined, empty object, empty string, empty array
 *
 * @param {any} value
 * @deprecated don't use this, just do normal checks
 * @return {boolean}
 */
function isEmpty(value: unknown): boolean {
    return (
        value == null
        || (typeof value === 'object' && Object.keys(value).length === 0)
        || (typeof value === 'string' && value.trim().length === 0)
        || (Array.isArray(value) && value.length === 0)
    );
}

/**
 * Removes none existing values from the values array.
 * Used to remove selected filter values that are no longer available in the filter data.
 *
 * @template T
 * @param {T[]} values
 * @param {T[]} existingValues
 * @return {T[]}
 */
function removeNoneExistingValues<T>(values: T[], existingValues: T[]): T[] {
    if (values.some(value => !existingValues.includes(value))) {
        return values.filter(value => existingValues.includes(value));
    }
    return values;
}

/**
 * Create a query string from an object
 *
 * @param {Record<string, string>} queryObject
 * @return {string}
 */
function createQueryString(queryObject: Record<string, string>): string {
    const str: string[] = [];

    Object.entries(queryObject).forEach(([key, value]) => {
        str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    });
    return `?${str.join('&')}`;
}

/**
 * Check if a key is a key of an object
 */
function isKeyOf<T extends object>(obj: T, key: PropertyKey): key is keyof T {
    return key in obj;
}

export {
    safeAverage,
    notEmpty,
    blur,
    getInputValue,
    sortArrayByProperty,
    formatAsPercentage,
    formatAsRoundedNumber,
    getPercentage,
    getInputValueNumber,
    isNumber,
    isEmpty,
    removeNoneExistingValues,
    createQueryString,
    isKeyOf
};
